import { isRecord } from "./ErrorParsers";
import {
  type ErrorDebugInfos,
  ErrorWithDebugInfos,
} from "./ErrorWithDebugInfos";

/**
 * A rule error of a project: an action is rejected for a reason the user can
 * understand and act on in normal use ("this code already exists"). The
 * project gives it a sentence, and that sentence is what the user reads. If a
 * developer has to look to make sense of a failure, it is not a rule: throw
 * `InternalError` (or another structured error) for it.
 */

/** `type` of every {@link UserFacingError}. No code or type maps to class `rule`; only the class itself does. */
export const USER_FACING_ERROR_TYPE = "rule";

export const USER_MESSAGE_KEY_MAX_LENGTH = 128;
export const USER_MESSAGE_TEXT_MAX_LENGTH = 500;
export const USER_MESSAGE_MAX_PARAMS = 10;
export const USER_MESSAGE_PARAM_NAME_MAX_LENGTH = 32;
export const USER_MESSAGE_PARAM_VALUE_MAX_LENGTH = 200;

/** Prefix of the key a rule error gets when it names none: `app.errors.<code>`. */
export const USER_MESSAGE_DEFAULT_KEY_PREFIX = "app.errors.";

/**
 * The values a sentence is filled with. Strings and finite numbers only: no
 * objects, lists, booleans or dates.
 */
export type UserMessageParams = Readonly<Record<string, string | number>>;

/**
 * What a rule error tells the user, as it travels to the client
 * (`extensions.userMessage`). Every part is limited, see
 * {@link readUserMessage}.
 */
export type UserMessage = Readonly<{
  /**
   * The i18n key of the sentence in the project's catalog. Absent when the
   * error named none and its code does not make one.
   */
  key?: string;
  /**
   * The sentence in the source language of the project, with `{{name}}`
   * placeholders for {@link UserMessage.params}.
   */
  defaultText?: string;
  params?: UserMessageParams;
}>;

export type UserFacingErrorOptions = Readonly<{
  /**
   * The sentence the user reads, in the source language of the project, with
   * `{{name}}` placeholders. Write the values into `params`, not into the
   * text: a value inside the text cannot be translated.
   */
  defaultText: string;
  /**
   * Values for the placeholders. They are shown to the acting user and sent
   * to the client as they are, so only pass what that user sent or may read
   * (a code, a name the user typed, a count). Never data of other users.
   */
  params?: UserMessageParams;
  /**
   * The i18n key of the sentence. Default: `app.errors.<code>`, when the code
   * consists of letters, digits and `_ / - .` only.
   */
  key?: string;
  /**
   * Details for the developer. They stay in the backend and are logged by
   * the list of keys of the framework only.
   */
  debugInfos?: ErrorDebugInfos;
}>;

/**
 * Marks an instance. A registered symbol, so that two copies of this package
 * in one process recognise each other's errors. It is defined non-enumerable:
 * spreading, `Object.assign`, `structuredClone` and JSON do not carry it, and
 * nothing parsed from a request or a response can have it.
 */
const BRAND = Symbol.for("@atoms/atom-types/UserFacingError");

const UNSAFE_NAMES: ReadonlySet<string> = new Set([
  "__proto__",
  "constructor",
  "prototype",
]);

function isLetter(character: string): boolean {
  return (
    (character >= "a" && character <= "z") ||
    (character >= "A" && character <= "Z")
  );
}

function isDigit(character: string): boolean {
  return character >= "0" && character <= "9";
}

function isKeySegment(segment: string): boolean {
  if (segment.length === 0 || UNSAFE_NAMES.has(segment)) {
    return false;
  }
  for (const character of segment) {
    const allowed =
      isLetter(character) ||
      isDigit(character) ||
      character === "_" ||
      character === "/" ||
      character === "-";
    if (!allowed) {
      return false;
    }
  }
  return true;
}

/**
 * Whether `value` can be the key of a user message: at most
 * {@link USER_MESSAGE_KEY_MAX_LENGTH} characters, segments of letters, digits
 * and `_ / -` separated by `.`, and no segment that names a part of the
 * object prototype.
 */
export function isUserMessageKey(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= USER_MESSAGE_KEY_MAX_LENGTH &&
    value.split(".").every(isKeySegment)
  );
}

/**
 * Whether `value` can be the name of a parameter: a letter, then letters,
 * digits and `_`, at most {@link USER_MESSAGE_PARAM_NAME_MAX_LENGTH}
 * characters.
 */
export function isUserMessageParamName(value: string): boolean {
  if (
    value.length === 0 ||
    value.length > USER_MESSAGE_PARAM_NAME_MAX_LENGTH ||
    UNSAFE_NAMES.has(value)
  ) {
    return false;
  }
  let isFirst = true;
  for (const character of value) {
    const allowed = isFirst
      ? isLetter(character)
      : isLetter(character) || isDigit(character) || character === "_";
    if (!allowed) {
      return false;
    }
    isFirst = false;
  }
  return true;
}

/**
 * Code point ranges that end a word in a line of text, as `[first, last]`:
 * control characters, every kind of space, line and paragraph separators,
 * the byte order mark, and the overrides, embeddings and isolates that change
 * the direction of the text that follows (U+202A to U+202E, U+2066 to
 * U+2069: they can make a sentence read differently from what it says). The
 * joiners inside a word (U+200C, U+200D) and the direction marks of
 * right-to-left text (U+061C, U+200E, U+200F) are not among them: scripts
 * and emoji need them.
 */
const SEPARATOR_RANGES: readonly (readonly [number, number])[] = [
  [0x00, 0x20],
  [0x7f, 0xa0],
  [0x1680, 0x1680],
  [0x2000, 0x200a],
  [0x2028, 0x202f],
  [0x205f, 0x205f],
  [0x2066, 0x2069],
  [0x3000, 0x3000],
  [0xfeff, 0xfeff],
];

function isSeparator(character: string): boolean {
  const code = character.codePointAt(0) ?? 0;
  return SEPARATOR_RANGES.some(
    ([first, last]) => code >= first && code <= last,
  );
}

/**
 * One line of text: every run of control characters, line breaks and spaces
 * becomes one space, leading and trailing ones are dropped, and the text is
 * cut at `maxLength` characters without splitting one. A user message is
 * shown line by line, so a line break inside a value would add a line of its
 * own. A client uses it on a sentence it is about to show.
 */
export function toUserMessageLine(text: string, maxLength: number): string {
  let line = "";
  let length = 0;
  let isSpacePending = false;
  for (const character of text) {
    if (isSeparator(character)) {
      isSpacePending = length > 0;
      continue;
    }
    const added = isSpacePending ? 2 : 1;
    if (length + added > maxLength) {
      break;
    }
    line += isSpacePending ? ` ${character}` : character;
    length += added;
    isSpacePending = false;
  }
  return line;
}

function readText(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const text = toUserMessageLine(value, USER_MESSAGE_TEXT_MAX_LENGTH);
  return text.length > 0 ? text : undefined;
}

function readParamValue(value: unknown): string | number | undefined {
  if (typeof value === "string") {
    return toUserMessageLine(value, USER_MESSAGE_PARAM_VALUE_MAX_LENGTH);
  }
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

function readParams(value: unknown): UserMessageParams | undefined {
  if (!isRecord(value) || Array.isArray(value)) {
    return undefined;
  }
  const params: Record<string, string | number> = {};
  let count = 0;
  for (const name of Object.keys(value)) {
    if (count >= USER_MESSAGE_MAX_PARAMS) {
      break;
    }
    const paramValue = isUserMessageParamName(name)
      ? readParamValue(value[name])
      : undefined;
    if (paramValue !== undefined) {
      params[name] = paramValue;
      count += 1;
    }
  }
  return count > 0 ? params : undefined;
}

/**
 * Reads a user message from anything and applies its limits. The one
 * implementation of them: the constructor of {@link UserFacingError} uses it,
 * the backend again before it sends the message, and a client on what it
 * receives. What does not fit is dropped, never an error:
 *
 * - `key`: see {@link isUserMessageKey}; otherwise there is no key.
 * - `defaultText`: a string, made one line and cut at
 *   {@link USER_MESSAGE_TEXT_MAX_LENGTH} characters.
 * - a part that cannot be read (a getter or a proxy that throws): nothing.
 * - `params`: at most {@link USER_MESSAGE_MAX_PARAMS} entries; the name as in
 *   {@link isUserMessageParamName}; the value a string (one line, cut at
 *   {@link USER_MESSAGE_PARAM_VALUE_MAX_LENGTH} characters) or a finite
 *   number.
 *
 * @returns the limited message, or nothing when `value` holds neither a key
 *   nor a text
 */
export function readUserMessage(value: unknown): UserMessage | undefined {
  try {
    return readUserMessageParts(value);
  } catch {
    // A getter or a proxy that throws: there is no message to read.
    return undefined;
  }
}

function readUserMessageParts(value: unknown): UserMessage | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  // Each part is read once: what was checked is what is returned.
  const candidateKey = value.key;
  const key = isUserMessageKey(candidateKey) ? candidateKey : undefined;
  const defaultText = readText(value.defaultText);
  if (key === undefined && defaultText === undefined) {
    return undefined;
  }
  const params = readParams(value.params);
  return {
    ...(key === undefined ? {} : { key }),
    ...(defaultText === undefined ? {} : { defaultText }),
    ...(params === undefined ? {} : { params }),
  };
}

const PLACEHOLDER_START = "{{";
const PLACEHOLDER_END = "}}";

/**
 * Fills the `{{name}}` placeholders of `template` with `params`, in one pass:
 * a filled-in value is not read again, so a value that contains a placeholder
 * stays text. Only own entries of `params` count. A placeholder without a
 * value is kept as it is written.
 */
export function fillUserMessageTemplate(
  template: string,
  params?: UserMessageParams,
): string {
  let filled = "";
  let position = 0;
  while (position < template.length) {
    const start = template.indexOf(PLACEHOLDER_START, position);
    const end =
      start === -1
        ? -1
        : template.indexOf(PLACEHOLDER_END, start + PLACEHOLDER_START.length);
    if (start === -1 || end === -1) {
      break;
    }
    const name = template.slice(start + PLACEHOLDER_START.length, end).trim();
    const afterPlaceholder = end + PLACEHOLDER_END.length;
    if (params !== undefined && Object.hasOwn(params, name)) {
      filled += `${template.slice(position, start)}${params[name]}`;
      position = afterPlaceholder;
    } else {
      // Not a placeholder of this message. Go on behind its opening braces,
      // so that `{{ {{code}}` still fills `{{code}}`.
      const afterStart = start + PLACEHOLDER_START.length;
      filled += template.slice(position, afterStart);
      position = afterStart;
    }
  }
  return filled + template.slice(position);
}

/**
 * The sentence of a user message in the source language: its default text
 * with the parameters filled in. Nothing without a default text.
 */
export function renderUserMessage(message: UserMessage): string | undefined {
  return message.defaultText === undefined
    ? undefined
    : fillUserMessageTemplate(message.defaultText, message.params);
}

/**
 * What a log line says in place of the message of a {@link UserFacingError}:
 * `[rule <key> params=<names of the parameters>]`, `[rule -]` without a key.
 * Key and names are limited to identifiers ({@link readUserMessage}), so
 * neither can carry content. The sentence and the values are for the user
 * and are never logged.
 */
export function describeUserMessageForLog(message: UserMessage): string {
  const limited = readUserMessage(message) ?? {};
  const names = Object.keys(limited.params ?? {});
  const params = names.length > 0 ? ` params=${names.join(",")}` : "";
  return `[rule ${limited.key ?? "-"}${params}]`;
}

function getDefaultKey(code: unknown): string | undefined {
  const key =
    typeof code === "string"
      ? `${USER_MESSAGE_DEFAULT_KEY_PREFIX}${code}`
      : undefined;
  return isUserMessageKey(key) ? key : undefined;
}

type ReadOptions = Readonly<{
  userMessage: UserMessage;
  debugInfos: ErrorDebugInfos;
}>;

/**
 * What the constructor takes from its options. Options that cannot be read
 * (a getter or a proxy that throws) count as none: the rule is still
 * rejected with its code.
 */
function readOptions(
  code: unknown,
  options: UserFacingErrorOptions | undefined,
): ReadOptions {
  try {
    const candidateKey = options?.key;
    const userMessage = readUserMessage({
      key: isUserMessageKey(candidateKey) ? candidateKey : getDefaultKey(code),
      defaultText: options?.defaultText,
      params: options?.params,
    });
    return {
      userMessage: userMessage ?? {},
      debugInfos: { ...options?.debugInfos },
    };
  } catch {
    return {
      userMessage: readUserMessage({ key: getDefaultKey(code) }) ?? {},
      debugInfos: {},
    };
  }
}

/**
 * Rejects an action with a rule of the project.
 *
 * ```ts
 * throw new UserFacingError("BPV_TREE_CODE_ALREADY_EXISTS", {
 *   defaultText: "Es existiert bereits ein Element mit dem Code {{code}}.",
 *   params: { code: newCode },
 * });
 * ```
 *
 * Over GraphQL (HTTP and WebSocket) such an error has class `rule`, and its
 * {@link UserMessage} travels in `extensions.userMessage`: the one way a
 * project sends text to a client. The GraphQL `message` is masked like that
 * of every other error. The backend logs the rejection as a warning with the
 * code, the key and the names of the parameters, never the sentence or a
 * value, and does not report it to error tracking.
 *
 * - `code`: a stable code of the project, declared in one place (an enum or
 *   a const object) with a prefix of the project (`BPV_…`, `pp51/…`). It is
 *   developer text and is sent and logged as written: never build it from
 *   request content.
 * - `message` (of the `Error`): the default text with the parameters filled
 *   in, for callers and tests inside the backend. It is text for the user:
 *   log it with `getErrorMessage` of `@atoms/atom-module`, which returns
 *   {@link describeUserMessageForLog} for this class, never directly.
 * - `stack`: its first line names the class and that log text, not the
 *   message, and its frames are written by the engine's own formatter, not
 *   by an `Error.prepareStackTrace` of the project: a logged stack holds
 *   nothing of the sentence.
 *
 * The constructor never throws: what does not fit the limits of
 * {@link readUserMessage} is dropped.
 */
export class UserFacingError extends ErrorWithDebugInfos {
  public readonly userMessage: UserMessage;

  constructor(code: string, options: UserFacingErrorOptions) {
    const { userMessage, debugInfos } = readOptions(code, options);
    super(
      USER_FACING_ERROR_TYPE,
      renderUserMessage(userMessage) ?? `rule rejected: ${code}`,
      { ...debugInfos, errorCode: code },
    );
    this.userMessage = userMessage;
    Object.defineProperty(this, BRAND, { value: true, enumerable: false });
    const stack = captureSafeStack(userMessage, new.target);
    try {
      this.stack = stack;
    } catch {
      // A frozen or sealed prototype chain: nothing more can be done here.
    }
  }
}

/** What V8 offers to capture a stack; other engines have neither. */
type StackCapturingErrorConstructor = {
  captureStackTrace?: (
    target: object,
    constructorOpt?: new (...args: never[]) => unknown,
  ) => void;
  prepareStackTrace?: unknown;
};

/**
 * The stack of a new rule error: the first line `UserFacingError: [rule …]`
 * and the frames of where it was created, without the constructor.
 *
 * The stack an engine gives an error starts with its message, which for this
 * class is the sentence with values of the request, and a project can
 * install a formatter (`Error.prepareStackTrace`) that writes the message
 * anywhere in it. So that stack is never read. The frames are captured
 * again, onto an object that only has the safe name and text, while the
 * project's formatter is switched off, so the engine's own formatter writes
 * them. All of it is synchronous: no other code runs while the formatter is
 * off, and it is put back whatever happens.
 *
 * It fails closed: on an engine without that API, or when any step throws,
 * the stack is the first line alone. So it is for an error built with
 * `Reflect.construct` and a `new.target` that is not on the call stack: the
 * engine then finds no frame to start from. A consequence: the frames of a rule
 * error are not rewritten by a formatter of the project (source maps that
 * are applied through `Error.prepareStackTrace`).
 */
function captureSafeStack(
  userMessage: UserMessage,
  constructorOpt: new (...args: never[]) => unknown,
): string {
  const text = describeUserMessageForLog(userMessage);
  const header = `UserFacingError: ${text}`;
  const errorConstructor: StackCapturingErrorConstructor = Error;
  if (typeof errorConstructor.captureStackTrace !== "function") {
    return header;
  }
  let hadFormatter = false;
  let formatter: unknown;
  let isFormatterOff = false;
  try {
    hadFormatter = Object.hasOwn(errorConstructor, "prepareStackTrace");
    formatter = errorConstructor.prepareStackTrace;
    errorConstructor.prepareStackTrace = undefined;
    isFormatterOff = true;
    const holder: { name: string; message: string; stack?: unknown } = {
      name: "UserFacingError",
      message: text,
    };
    errorConstructor.captureStackTrace(holder, constructorOpt);
    // Read here, while the formatter is off: an engine writes the text of a
    // stack when it is first read.
    const { stack } = holder;
    const isAsExpected =
      typeof stack === "string" &&
      stack.startsWith(header) &&
      (stack.length === header.length || stack[header.length] === "\n");
    return isAsExpected ? stack : header;
  } catch {
    return header;
  } finally {
    if (isFormatterOff) {
      restoreStackFormatter(errorConstructor, hadFormatter, formatter);
    }
  }
}

/** Puts `Error.prepareStackTrace` back as it was; never throws. */
function restoreStackFormatter(
  errorConstructor: StackCapturingErrorConstructor,
  hadFormatter: boolean,
  formatter: unknown,
): void {
  try {
    if (hadFormatter) {
      errorConstructor.prepareStackTrace = formatter;
    } else {
      delete errorConstructor.prepareStackTrace;
    }
  } catch {
    // The property cannot be written (a frozen `Error`): then it could not
    // be switched off either.
  }
}

/** Whether the constructor marked `error`. False for what throws when it is inspected. */
function isMarked(error: unknown): error is Error {
  try {
    return (
      error instanceof Error &&
      Object.hasOwn(error, BRAND) &&
      Reflect.get(error, BRAND) === true
    );
  } catch {
    return false;
  }
}

/**
 * Whether `error` is a {@link UserFacingError}: an `Error` that the
 * constructor marked. A copy of one (spread, `Object.assign`,
 * `structuredClone`, JSON) is not, and neither is an object that only looks
 * like one. It stays one when its user message is replaced or cannot be
 * read: its `message` is text for the user either way.
 */
export function isUserFacingError(error: unknown): error is UserFacingError {
  return isMarked(error);
}

/**
 * The user message of a {@link UserFacingError}, read once and with the
 * limits applied again: the property is public, and a subclass can assign to
 * it. An empty message when nothing valid is left or it cannot be read.
 * Nothing for every other value.
 */
export function getUserMessage(error: unknown): UserMessage | undefined {
  if (!isMarked(error)) {
    return undefined;
  }
  try {
    return readUserMessage(Reflect.get(error, "userMessage")) ?? {};
  } catch {
    return {};
  }
}
